import { ENDPOINTS, getFullUrl } from "./config";

export interface SearchAnalyticsItem {
  id: number;
  patientId: string;
  nearestClinicId: string;
  searchText: string;
  selectedItemId: string;
  selectedItemType: "PRODUCT" | "LABTEST" | "DOCTOR" | string;
  city: string;
  state: string;
  createdAt: string;
  patientName: string;
  patientPhoneNumber: string;
  patientImage?: string;
  clinicName: string;
  clinicPhoneNumber: string;
  selectedItemDetails: any;
}

export interface ClinicSearchBreakdown {
  clinicId: string;
  clinicName: string;
  cityName: string;
  totalSearches: number;
  doctorsCount: number;
  productsCount: number;
  labtestsCount: number;
}

export interface SearchAnalyticsDashboardData {
  totalSearches: number;
  doctorsSearchedCount: number;
  productsSearchedCount: number;
  labtestsSearchedCount: number;
  clinicBreakdown: ClinicSearchBreakdown[];
}

export interface ReviewItem {
  review_id: number | string;
  user_id: string;
  entity_type: string;
  entity_id: string;
  order_id?: string;
  rating: number;
  comment: string;
  created_at: string;
  updated_at?: string;
  patientName?: string;
  patientPhoneNumber?: string;
  patientImage?: string;
  entityDetails?: any;
}

export interface EntityTypeBreakdown {
  entityType: string;
  totalReviews: number;
  averageRating: number;
}

export interface ReviewAnalyticsDashboardData {
  totalReviews: number;
  averageRating: number;
  ratingBreakdown: {
    [key: string]: number;
    "1"?: number;
    "2"?: number;
    "3"?: number;
    "4"?: number;
    "5"?: number;
  };
  entityTypeBreakdown: EntityTypeBreakdown[];
  doctorReviewsCount?: number;
  productReviewsCount?: number;
  labtestReviewsCount?: number;
  appointmentReviewsCount?: number;
}

export interface ReviewListParams {
  page?: number;
  pageSize?: number;
  entityType?: string;
  entityId?: string;
  rating?: number;
  userId?: string;
  orderId?: string;
  searchText?: string;
  startDate?: string;
  endDate?: string;
}

export interface ReviewDashboardParams {
  entityType?: string;
  entityId?: string;
  rating?: number;
  userId?: string;
  startDate?: string;
  endDate?: string;
}

export class AnalyticsService {
  static getToken(): string | null {
    return localStorage.getItem("token");
  }

  static async getSearchAnalytics(page: number = 1, pageSize: number = 10) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.ANALYTICS.SEARCH_ANALYTICS));
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken() || ""}`,
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch search analytics",
      };
    }
  }

  static async getSearchAnalyticsDashboard() {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.ANALYTICS.SEARCH_ANALYTICS_DASHBOARD), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken() || ""}`,
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch search analytics dashboard",
      };
    }
  }

  static async getReviews(params: ReviewListParams = {}) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.ANALYTICS.REVIEW_ANALYTICS));
      if (params.page !== undefined) url.searchParams.append("page", params.page.toString());
      if (params.pageSize !== undefined) url.searchParams.append("pageSize", params.pageSize.toString());
      if (params.entityType) url.searchParams.append("entityType", params.entityType);
      if (params.entityId) url.searchParams.append("entityId", params.entityId);
      if (params.rating !== undefined && params.rating !== null) {
        url.searchParams.append("rating", params.rating.toString());
      }
      if (params.userId) url.searchParams.append("userId", params.userId);
      if (params.orderId) url.searchParams.append("orderId", params.orderId);
      if (params.searchText) url.searchParams.append("searchText", params.searchText);
      if (params.startDate) url.searchParams.append("startDate", params.startDate);
      if (params.endDate) url.searchParams.append("endDate", params.endDate);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken() || ""}`,
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch reviews",
      };
    }
  }

  static async getReviewDashboard(params: ReviewDashboardParams = {}) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.ANALYTICS.REVIEW_ANALYTICS_DASHBOARD));
      if (params.entityType) url.searchParams.append("entityType", params.entityType);
      if (params.entityId) url.searchParams.append("entityId", params.entityId);
      if (params.rating !== undefined && params.rating !== null) {
        url.searchParams.append("rating", params.rating.toString());
      }
      if (params.userId) url.searchParams.append("userId", params.userId);
      if (params.startDate) url.searchParams.append("startDate", params.startDate);
      if (params.endDate) url.searchParams.append("endDate", params.endDate);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken() || ""}`,
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch review dashboard analytics",
      };
    }
  }
}

